/**
 * The strategic UI's modal overlay (M4 plan I6): the "While you were away" report, drawn with React in a `.sui-layer`
 * right after `#overlay` (`mountLayerAfter`, so it shows in flight and when docked). The Reach Wire panel moved to
 * the dashboard (Intel › Wire, W3 card M1); this host keeps only the away report.
 *
 * - Opening holds `ui.modal` with an Esc handler (`enterModal`: flight input zeroed, pointer lock released) and,
 *   over the pause menu, takes the menu's inputs (`setMenuCover`).
 * - Keyboard: a window capture listener (registered before the pause menu's) takes every trusted key while the
 *   overlay is open: Esc closes, arrows and Tab move through the buttons, Enter and Space press the focused one, and
 *   nothing leaks into the flight bindings or the menu.
 * - Pad: the owner feeds `MenuInput`s from `stepPadNav` into `input`.
 *
 * UI events only.
 */
import type { UiState } from '../../app/feature'
import { enterModal } from '../../app/ui'
import { setMenuCover } from '../../hud/menu'
import type { AwayReport as AwayReportData } from '../../strategic/types'
import { AwayReport } from './AwayReport'
import { awayReportModel } from './awayReportModel'
import { mountLayerAfter, navigateButtons, type NavInput, type ReactMount } from './mount'
import type { SystemNameFn } from './wireModel'

export type OverlayKind = 'away'

export interface OverlayHostOptions {
  root: HTMLElement
  ui: UiState
  /** True while the game is paused (the pause menu is up): closing then never asks for pointer lock. */
  paused?: () => boolean
  /** Window for the key listener (tests pass a fake target). */
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>
}

export interface OverlayHost {
  readonly open: OverlayKind | null
  /** Shows the away report. */
  openAway(report: AwayReportData, stationName: (id: string) => string | null, systemName?: SystemNameFn): void
  /** Closes the open overlay; `relock` asks for pointer lock again when no other modal is open. */
  close(relock?: boolean): void
  /** A pad or menu input for the open overlay. */
  input(i: NavInput): void
  dispose(): void
}

const KEY_INPUT: Readonly<Record<string, NavInput>> = Object.freeze({
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  Enter: 'activate',
  NumpadEnter: 'activate',
  Space: 'activate',
})

export function createOverlayHost(opts: OverlayHostOptions): OverlayHost {
  let kind: OverlayKind | null = null
  let mount: ReactMount | null = null
  let leave: ((relock?: boolean) => void) | null = null
  const target = opts.target ?? (typeof window === 'undefined' ? null : window)

  function onEscape(): boolean {
    host.close()
    return true
  }

  function begin(next: OverlayKind): ReactMount {
    if (mount === null) {
      mount = mountLayerAfter(opts.root, 'sui-layer')
      leave = enterModal(opts.ui, onEscape)
      setMenuCover(true)
    }
    kind = next
    return mount
  }

  function focusFirst(m: ReactMount): void {
    const b = m.el.querySelector<HTMLButtonElement>('[data-testid="away-continue"]') ?? m.el.querySelector<HTMLButtonElement>('button:not([disabled])')
    b?.focus()
  }

  function onKey(e: KeyboardEvent): void {
    if (mount === null || !e.isTrusted) return
    if (e.code === 'Escape') {
      e.preventDefault()
      e.stopImmediatePropagation()
      if (!e.repeat) host.close()
      return
    }
    e.stopImmediatePropagation()
    const i = e.code === 'Tab' ? (e.shiftKey ? 'up' : 'down') : KEY_INPUT[e.code]
    if (i === undefined) return
    e.preventDefault()
    navigateButtons(mount.el, i)
  }

  target?.addEventListener('keydown', onKey, { capture: true })

  const host: OverlayHost = {
    get open(): OverlayKind | null {
      return kind
    },
    openAway(report: AwayReportData, stationName: (id: string) => string | null, systemName?: SystemNameFn): void {
      const m = begin('away')
      m.render(<AwayReport model={awayReportModel(report, stationName, systemName)} onContinue={() => host.close()} />)
      focusFirst(m)
    },
    close(relock = true): void {
      if (mount === null) return
      mount.unmount()
      mount = null
      kind = null
      setMenuCover(false)
      const l = leave
      leave = null
      l?.(relock && opts.paused?.() !== true)
    },
    input(i: NavInput): void {
      if (mount === null) return
      if (i === 'back') {
        host.close()
        return
      }
      navigateButtons(mount.el, i)
    },
    dispose(): void {
      host.close(false)
      target?.removeEventListener('keydown', onKey, { capture: true })
    },
  }
  return host
}
